import { useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'

type Options<T extends { id: string }> = {
  items: T[]
  /** true až po načtení seznamu (ne během loading). */
  ready: boolean
  onFound: (item: T) => void
  onNotFound?: (id: string) => void
  /** Vrátí DOM id pro scrollIntoView (např. `ticket-${id}`). */
  elementIdFor?: (id: string) => string
}

/**
 * Deep-link `?id=` z ranního digestu / Můj kabinet:
 * najde položku v načteném seznamu, zavolá onFound, odstraní id z URL.
 */
export function useDeepLinkById<T extends { id: string }>(options: Options<T>): void {
  const [searchParams, setSearchParams] = useSearchParams()
  const handled = useRef<string | null>(null)
  const { items, ready, onFound, onNotFound, elementIdFor } = options

  useEffect(() => {
    const id = searchParams.get('id')
    if (!id || !ready) return
    if (handled.current === id) return
    handled.current = id

    const found = items.find((x) => x.id === id)
    if (found) {
      onFound(found)
      if (elementIdFor) {
        const elId = elementIdFor(id)
        window.setTimeout(() => {
          document.getElementById(elId)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }, 150)
      }
    } else {
      onNotFound?.(id)
    }

    const next = new URLSearchParams(searchParams)
    next.delete('id')
    setSearchParams(next, { replace: true })
    // onFound/onNotFound záměrně mimo deps — volající předává inline callback
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, ready, items, setSearchParams])
}
